import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Calendar, Clock, Mail, Trash2, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 10;

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const formatDateTime = (dateStr) =>
  new Date(dateStr).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const getStatusBadge = (date, t) => {
  const today = new Date().toISOString().split('T')[0];
  if (date === today) return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{t('dashboard.today')}</span>;
  if (date > today) return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">{t('dashboard.upcoming')}</span>;
  return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">{t('dashboard.past')}</span>;
};

const AdminTab = () => {
  const { t } = useLanguage();

  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [consultationPage, setConsultationPage] = useState(0);
  const [contactPage, setContactPage] = useState(0);
  const [consultationTotal, setConsultationTotal] = useState(0);
  const [contactTotal, setContactTotal] = useState(0);
  const [subTab, setSubTab] = useState('consultations');
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [notification, setNotification] = useState(null);

  const showNotif = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch { /* server-side */ }
  }, []);

  const fetchConsultations = useCallback(async () => {
    try {
      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const res = await fetch(`${API_URL}/api/admin/consultations?limit=${ITEMS_PER_PAGE}&skip=${consultationPage * ITEMS_PER_PAGE}${statusParam}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setConsultations(d.consultations);
        setConsultationTotal(d.total);
      }
    } catch { /* server-side */ }
  }, [filter, consultationPage]);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/contacts?limit=${ITEMS_PER_PAGE}&skip=${contactPage * ITEMS_PER_PAGE}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setContacts(d.contacts);
        setContactTotal(d.total);
      }
    } catch { /* server-side */ }
  }, [contactPage]);

  const fetchScheduler = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/scheduler-status`, { credentials: 'include' });
      if (res.ok) setSchedulerStatus(await res.json());
    } catch { /* server-side */ }
  }, []);

  useEffect(() => { fetchStats(); fetchScheduler(); }, [fetchStats, fetchScheduler]);
  useEffect(() => { fetchConsultations(); }, [fetchConsultations]);
  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const deleteConsultation = async (id) => {
    if (!window.confirm('Delete this consultation?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/consultations/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { showNotif('Deleted'); fetchConsultations(); fetchStats(); }
    } catch { showNotif('Failed', 'error'); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/contacts/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { showNotif('Deleted'); fetchContacts(); fetchStats(); }
    } catch { showNotif('Failed', 'error'); }
  };

  const triggerReminders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/trigger-reminders`, { method: 'POST', credentials: 'include' });
      if (res.ok) showNotif('Reminders triggered');
    } catch { showNotif('Failed', 'error'); }
  };

  return (
    <div data-testid="admin-tab">
      {notification && (
        <div className={`fixed top-24 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm ${notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
          {notification.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {notification.msg}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Bookings', value: stats.total_consultations, icon: Calendar, color: 'blue' },
            { label: t('dashboard.upcoming'), value: stats.upcoming_consultations, icon: Clock, color: 'green' },
            { label: t('dashboard.today'), value: stats.today_consultations, icon: Calendar, color: 'orange' },
            { label: t('dashboard.past'), value: stats.past_consultations, icon: Calendar, color: 'slate' },
            { label: 'Contact Forms', value: stats.total_contacts, icon: Mail, color: 'purple' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-${s.color}-100 rounded-lg`}><s.icon className={`w-5 h-5 text-${s.color}-600`} /></div>
                <div>
                  <p className="text-2xl font-bold text-[#0d1442]">{s.value}</p>
                  <p className="text-sm text-slate-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scheduler */}
      {schedulerStatus && (
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500' : 'bg-red-500'}`} />
              <div>
                <p className="font-medium text-[#0d1442]">Automated Reminder System</p>
                <p className="text-sm text-slate-500">
                  {schedulerStatus.scheduler_running ? 'Running' : 'Stopped'}
                  {schedulerStatus.next_run && ` | Next: ${schedulerStatus.next_run.split('.')[0].replace('T', ' ')}`}
                </p>
              </div>
            </div>
            <button onClick={triggerReminders} className="px-4 py-2 text-sm bg-[#1a237e] text-white rounded-lg hover:bg-[#0d1442] transition-colors">
              Send Reminders Now
            </button>
          </div>
        </div>
      )}

      {/* Sub-tabs + Export */}
      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <button onClick={() => setSubTab('consultations')} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${subTab === 'consultations' ? 'bg-[#1a237e] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'}`}>
          <Calendar className="w-4 h-4" />Consultations
        </button>
        <button onClick={() => setSubTab('contacts')} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${subTab === 'contacts' ? 'bg-[#1a237e] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'}`}>
          <Mail className="w-4 h-4" />Contact Forms
        </button>
        <div className="ml-auto flex gap-2">
          <button onClick={() => window.open(`${API_URL}/api/admin/export/consultations`, '_blank')} data-testid="export-consultations-btn" className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download className="w-3.5 h-3.5" />{t('dashboard.exportCSV')} (Bookings)
          </button>
          <button onClick={() => window.open(`${API_URL}/api/admin/export/contacts`, '_blank')} data-testid="export-contacts-btn" className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download className="w-3.5 h-3.5" />{t('dashboard.exportCSV')} (Contacts)
          </button>
        </div>
      </div>

      {/* Consultations Table */}
      {subTab === 'consultations' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-[#0d1442]">Consultation Bookings</h2>
            <div className="flex gap-2">
              {['all', 'upcoming', 'today', 'past'].map(f => (
                <button key={f} onClick={() => { setFilter(f); setConsultationPage(0); }} className={`px-3 py-1.5 text-sm rounded-lg capitalize transition-colors ${filter === f ? 'bg-[#1a237e] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{f}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Reminder</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {consultations.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[#0d1442]">{formatDate(c.date)}</div>
                      <div className="text-sm text-slate-500">{c.time_slot}</div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-[#0d1442]">{c.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{c.company_name}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-600">{c.email}</div>
                      {c.phone && <div className="text-sm text-slate-500">{c.phone}</div>}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(c.date, t)}</td>
                    <td className="px-6 py-4">
                      {c.reminder_sent
                        ? <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">{t('dashboard.sent')}</span>
                        : <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">{t('dashboard.pending')}</span>}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => deleteConsultation(c.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {consultationTotal > 0 ? `${consultationPage * ITEMS_PER_PAGE + 1} - ${Math.min((consultationPage + 1) * ITEMS_PER_PAGE, consultationTotal)} of ${consultationTotal}` : '0 items'}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConsultationPage(Math.max(0, consultationPage - 1))} disabled={consultationPage === 0} className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setConsultationPage(consultationPage + 1)} disabled={(consultationPage + 1) * ITEMS_PER_PAGE >= consultationTotal} className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}

      {/* Contacts Table */}
      {subTab === 'contacts' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-semibold text-[#0d1442]">Contact Form Submissions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Message</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {contacts.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDateTime(c.created_at)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-[#0d1442]">{c.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{c.company_name}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-600">{c.email}</div>
                      {c.phone && <div className="text-sm text-slate-500">{c.phone}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate" title={c.message}>{c.message}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => deleteContact(c.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {contactTotal > 0 ? `${contactPage * ITEMS_PER_PAGE + 1} - ${Math.min((contactPage + 1) * ITEMS_PER_PAGE, contactTotal)} of ${contactTotal}` : '0 items'}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setContactPage(Math.max(0, contactPage - 1))} disabled={contactPage === 0} className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setContactPage(contactPage + 1)} disabled={(contactPage + 1) * ITEMS_PER_PAGE >= contactTotal} className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTab;
